import * as Yup from 'yup';
import moment, { duration } from 'moment';

function equalTo(ref: any, msg: string) {
    return this.test({
        name: 'equalTo',
        exclusive: false,
        message: msg || 'Passwords must match',
        params: {
            reference: ref.path
        },
        test: function (value) {
            return value === this.resolve(ref)
        }
    })
};

declare module 'yup' {
    interface StringSchema {
        equalTo: (ref: Yup.Ref | string, msg?: string) => StringSchema;
    }
}

Yup.addMethod(Yup.string, 'equalTo', equalTo);

const resetPasswordShape = {
    password: Yup.string().required('Password is required').min(6, "Password must be at least 6 characters long"),
    checkPassword: Yup.string().equalTo(Yup.ref('password')),
}
const phoneRegExp = /^[0-9+\- ][0-9- ]*$/;
const aocNumberRegExp = /^[a-zA-Z0-9]+$/;

const resetMyPasswordShape = {
    originalPassword: Yup.string().required('Original Password is required'),
    password: Yup.string().required('Password is required').min(6, "Password must be at least 6 characters long"),
    checkPassword: Yup.string().equalTo(Yup.ref('password')),
}

const allowedActionsShape = {
    entityType: Yup.string().required('Entity Type is required'),
    allowedRole: Yup.string().required('Role is required'),
    entityState: Yup.string().required('State is required'),
    nextState: Yup.string().required('Next is required'),
    actionName: Yup.string().required('Name is required'),
    actionType: Yup.string().required(),
    actionDescription: Yup.string(),
    condition: Yup.string()
}
let allowedActionsShapeYup = () => Yup.object().shape(allowedActionsShape);
const clientStaffShape = {
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Email looks a bit strange...'),
    phoneNumber: Yup.string().required("Phone number is required").matches(phoneRegExp, 'Phone number is not valid'),
}
const adminShape = {
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Email looks a bit strange...'),
}
const adminClientStaffShape = {
    canViewSupplierInfo: Yup.boolean(),
}

const newEmailShape = {

    newEmail: Yup.string().email('Email looks a bit strange...'),
}

let clientStaffShapeYup = () => Yup.object().shape({ ...clientStaffShape, ...adminClientStaffShape });
let adminShapeYup = () => Yup.object().shape({ ...adminShape });
let resetPasswordShapeYup = () => Yup.object().shape(resetPasswordShape);
let resetMyPasswordShapeYup = () => Yup.object().shape(resetMyPasswordShape);
let newEmailShapeYup = () => Yup.object().shape(newEmailShape);

const clientDepartmentShape = {
    clientMasterId: Yup.string().required("Parent Client is required."),
    firstName: Yup.string().required('First Name is required.'),
    lastName: Yup.string().required('Last Name is required.'),
    departmentPhone: Yup.string().required('Department  Phone is required.').matches(phoneRegExp, 'Phone number is not valid'),

    email: Yup.string().email().required('Email is required.'),
    notesForSupplier: Yup.string(),
    emailDomain: Yup.string(),

}
const adminClientDepartmentShape = {
    canViewSupplierInfo: Yup.boolean(),
    hidePrice: Yup.boolean(),
}

let clientDepartmentShapeYup = () => Yup.object().shape({ ...clientDepartmentShape, ...adminClientDepartmentShape });
const clientShape = {
    companyName: Yup.string().required('Company Name is required.'),
    companyAddress: Yup.string().required('Company Address is required.'),
    companyPhone: Yup.string().required('Company  Phone is required.').matches(phoneRegExp, 'Phone number is not valid'),
    firstName: Yup.string().required('First Name is required.'),
    lastName: Yup.string().required('Last Name is required.'),
    email: Yup.string().email().required('Email is required.'),
    notesForSupplier: Yup.string(),

}
let inviteClientStaffShapeYup = () => Yup.object().shape({ ...clientStaffShape, ...resetPasswordShape });
let inviteClientDepartmentShapeYup = () => Yup.object().shape({ ...clientDepartmentShape, ...resetPasswordShape });
let inviteClientShapeYup = () => Yup.object().shape({ ...clientShape, ...resetPasswordShape });
let inviteSupplierShapeYup = () => Yup.object().shape({
    ...baseSupplierShape,
    ...resetPasswordShape
}).test(
    'one-of-aoc-or-part138',
    'Exactly one of AOC Number or Part 138 Number must be provided',
    function (value) {
        const aoc = value && typeof value === 'object' && 'aocNumber' in value ? value.aocNumber?.trim() : '';
        const part138 = value && typeof value === 'object' && 'part138Number' in value ? value.part138Number?.trim() : '';
        const valid = (!!aoc && !part138) || (!aoc && !!part138);
        if (!valid) {
            this.createError({ path: 'aocNumber', message: 'Exactly one of AOC Number or Part 138 Number must be provided' });
            this.createError({ path: 'part138Number', message: 'Exactly one of AOC Number or Part 138 Number must be provided' });
            return false;
        }
        return true;
    }
);
let inviteAdminShapeYup = () => Yup.object().shape({ ...adminShape, ...resetPasswordShape })
let inviteCharterAdminShapeYup = () => Yup.object().shape({ ...clientShape, ...resetPasswordShape });

const adminClientShape = {
    emailDomain: Yup.string(),
    canViewSupplierInfo: Yup.boolean(),
    hidePrice: Yup.boolean(),
    clientType: Yup.string().required('Client Type is required.'),
}

let clientShapeYup = () => Yup.object().shape({ ...clientShape, ...adminClientShape });
// --- Define base and admin shapes if not already defined ---
const baseSupplierShape = {
    companyName: Yup.string().required('Trading Name is required.'),
    companyAddress: Yup.string().required('Address is required.'),
    companyPhone: Yup.string().required('Phone is required.').matches(phoneRegExp, 'Phone number is not valid'),
    firstName: Yup.string().required('First Name is required.'),
    lastName: Yup.string().required('Last Name is required.'),
    email: Yup.string().email().required('Email is required.'),
    clientTypes: Yup.string().required('Client Type is required'),
    charterTypes: Yup.string().required('Charter Types is required.'),
    aocIssuedDate: Yup.mixed()
        .transform((value, originalValue) => {
            // Handle empty values
            if (!originalValue || (typeof originalValue === 'string' && !originalValue.trim())) {
                return null;
            }
            // If it's already a valid Date object
            if (value instanceof Date && !isNaN(value.getTime())) {
                return value;
            }
            // Try parsing with moment
            const momentDate = moment(originalValue);
            if (momentDate.isValid()) {
                return momentDate.toDate();
            }

            // Return null for invalid dates
            return null;
        }),
    aocNumber: Yup.string()
        .trim()
        .matches(aocNumberRegExp, 'AOC Number must be alphanumeric only')
        .max(50, 'AOC Number must be at most 50 characters')
        // .test('at-least-one', 'Please provide either AOC Number and Date or Part 138 Number and Date', function(value) {
        //     const { part138Number, part138IssuedDate, aocIssuedDate } = this.parent;
        //     const aocNumber = value?.trim();
        //     const part138Num = part138Number?.trim();
            
        //     const hasAocPair = aocNumber && aocIssuedDate instanceof Date && !isNaN(aocIssuedDate.getTime());
        //     const hasPart138Pair = part138Num && part138IssuedDate instanceof Date && !isNaN(part138IssuedDate.getTime());
            
        //     return hasAocPair || hasPart138Pair;
        // })
        ,
    part138IssuedDate: Yup.mixed()
        .transform((value, originalValue) => {
            // Handle empty values
            if (!originalValue || (typeof originalValue === 'string' && !originalValue.trim())) {
                return null;
            }

            // If it's already a valid Date object
            if (value instanceof Date && !isNaN(value.getTime())) {
                return value;
            }

            // Try parsing with moment
            const momentDate = moment(originalValue);
            if (momentDate.isValid()) {
                return momentDate.toDate();
            }

            // Return null for invalid dates
            return null;
        }),
    part138Number: Yup.string()
        .trim()
        .matches(aocNumberRegExp, 'Part 138 Number must be alphanumeric only')
        .max(50, 'Part 138 Number must be at most 50 characters')
        // .test('at-least-one', 'Please provide either AOC Number and Date or Part 138 Number and Date', function(value) {
        //     const { aocNumber, aocIssuedDate, part138IssuedDate } = this.parent;
        //     const part138Num = value?.trim();
        //     const aocNum = aocNumber?.trim();
            
        //     const hasAocPair = aocNum && aocIssuedDate instanceof Date && !isNaN(aocIssuedDate.getTime());
        //     const hasPart138Pair = part138Num && part138IssuedDate instanceof Date && !isNaN(part138IssuedDate.getTime());
            
        //     return hasAocPair || hasPart138Pair;
        // }),
};

let supplierShapeYup = () => Yup.object().shape({
    ...baseSupplierShape
    /*}).test('at-least-one-pair', '', function(value) {
    const aocNumber = value?.aocNumber?.trim();
    const aocDate = value?.aocIssuedDate;
    const part138Number = value?.part138Number?.trim();
    const part138Date = value?.part138IssuedDate;

    const hasAocPair = aocNumber && aocDate instanceof Date && !isNaN(aocDate.getTime());
    const hasPart138Pair = part138Number && part138Date instanceof Date && !isNaN(part138Date.getTime());

    if (!hasAocPair && !hasPart138Pair) {
        throw this.createError({
            path: 'aocNumber',
            message: 'Please provide either AOC Number and Date or Part 138 Number and Date'
        }) || 
        this.createError({
            path: 'part138Number',
            message: 'Please provide either AOC Number and Date or Part 138 Number and Date'
        });
    }
    return true;
}).test({
    name: 'highlight-both-fields',
    message: 'Please provide either AOC Number and Date or Part 138 Number and Date',
    test: function(value) {
        const aocNumber = value?.aocNumber?.trim();
        const aocDate = value?.aocIssuedDate;
        const part138Number = value?.part138Number?.trim();
        const part138Date = value?.part138IssuedDate;

        const hasAocPair = aocNumber && aocDate instanceof Date && !isNaN(aocDate.getTime());
        const hasPart138Pair = part138Number && part138Date instanceof Date && !isNaN(part138Date.getTime());

        if (!hasAocPair && !hasPart138Pair) {
            return false;
        }
        return true;
    },
    exclusive: false */
});

const indicativePriceShape = {
    from: Yup.string().required("From is required"),
    to: Yup.string().required("To is required"),
    preferredAircraft: Yup.array().required("Preferred Aircraft required"),
}

const emailIndicativeShape = {
    email: Yup.string().email('Email looks a bit strange...').required("Email is reuquired"),
    ...indicativePriceShape,
}

let indicativePriceShapeYup = () => Yup.object().shape(indicativePriceShape);
let emailIndicativeShapeYup = () => Yup.object().shape(emailIndicativeShape);

const manifestShape = {
    passengerName: Yup.string().required('Passenger name required'),
    contactNumber: Yup.string().matches(phoneRegExp, 'Contact number is not valid'),
    emailAddress: Yup.string().email('Email looks a bit strange...'),
   // passengerWeight: Yup.number().required("Passenger weight required").positive("Passenger weight required"),
    passengerWeight: Yup.number().when('cargoWeight',{             
        is:(cargoWeight)=>(cargoWeight && cargoWeight !==0), 
        then: Yup.number().notRequired(),
        otherwise:Yup.number().required("Passenger weight required").positive("Passenger weight required")
    }),
    freightWeight: Yup.number().typeError("Freight Weight must be a number"),
    cargoWeight: Yup.number().typeError("Cargo Weight must be a number"),
};

let manifestShapeYup = Yup.object().shape(manifestShape);

const normalizeYupDate = (value: any) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    if (typeof value?.toDate === 'function') return value.toDate();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const passengerRangeShape = (label) => Yup.number()
    .nullable()
    .transform((value, originalValue) => originalValue == null || (typeof originalValue === 'string' && originalValue.trim() === '') ? undefined : value)
    .typeError(`${label} must be a number`)
    .when('passengerRange', {
        is: true,
        then: Yup.number()
            .transform((value, originalValue) => originalValue == null || (typeof originalValue === 'string' && originalValue.trim() === '') ? undefined : value)
            .typeError(`${label} must be a number`)
            .required(`${label} is required`)
            .integer(`${label} must be a whole number`)
            .moreThan(0, `${label} must be greater than zero`),
    });

const createRouteShape = {
    viewDepartDate: Yup.date().nullable().transform((value, originalValue) => {
        return normalizeYupDate(originalValue);
    }).required("Depart Date is required"),
    viewDepartTime: Yup.date().nullable().transform((value, originalValue) => {
        return normalizeYupDate(originalValue);
    }).required("Depart Time is required"),
    from: Yup.string().nullable().required("From is required"),
    to: Yup.string().nullable().required("To is required"),
    fromState: Yup.string().nullable().when('fromStateNew', {
        is: true,
        then: Yup.string().nullable().trim().required('From State is required'),
    }),
    toState: Yup.string().nullable().when('toStateNew', {
        is: true,
        then: Yup.string().nullable().trim().required('To State is required'),
    }),
    passengerNumber: Yup.number(),
    passengerRange: Yup.boolean(),
    passengerRange_Max: passengerRangeShape('Passenger Range To').when('passengerRange', {
        is: true,
        then: schema => schema.test(
            'passenger-range-order',
            'Passenger Range To must be greater than or equal to Passenger Range From',
            function (max) {
                const min = this.parent.passengerRange_Min;
                return !Number.isFinite(min) || !Number.isFinite(max) || max >= min;
            }
        ),
    }),
    passengerRange_Min: passengerRangeShape('Passenger Range From'),
    payload: Yup.number().moreThan(-1, "Please supply available payload"),
    manifest: Yup.array().of(
        Yup.object().shape(manifestShape))

};
let createRouteShapeYup = Yup.object().shape(createRouteShape);

const changeCharterShape = {
    // Optional text columns can come back from the API as null
    purchaseOrderNumber: Yup.string().nullable(),
    jobReference: Yup.string(),
    catering: Yup.string().required(),
    charterSubType: Yup.string().required('Charter SubType is required'),
    charterType: Yup.string().required('Charter Type is required'),
    clientId: Yup.string().required('Client is required'),
    dangerousGoods: Yup.boolean().required(),
    dangerousGoodsIds: Yup.string().nullable(),
    preferredAircraft: Yup.string().nullable(),
    specialRequests: Yup.string().nullable(),
    routes: Yup.array().of(
        Yup.object().shape(createRouteShape)).required("Must have route")

};
const charterRequestShape = {
    ...changeCharterShape,
    shared: Yup.boolean().required(),
};
const manifestItemShape = {
    manifest: Yup.array().of(
        Yup.object().shape(manifestShape))
}
const charterRequestManifestShape = {
    routes: Yup.array().of(
        Yup.object().shape(manifestItemShape)).required("Must have route")

};

let charterRequestManifestShapeYup = Yup.object().shape(charterRequestManifestShape);


const createCharterRequestShape = {
    biddingTimeSpanHours: Yup.number().required('Bidding to close in (hours) is required').positive().integer(),
    ...charterRequestShape,
}

const passengerLimitShape = Yup.number()
    .transform((value, originalValue) => originalValue == null || (typeof originalValue === 'string' && originalValue.trim() === '') ? undefined : value)
    .typeError('Passenger limit must be a number')
    .required('Passenger limit is required')
    .integer('Passenger limit must be a whole number')
    .moreThan(0, 'Passenger limit must be greater than zero');

const supplierChangeCharterRouteShape = {
    ...createRouteShape,
    flightTime: Yup.string().required("Flight Time is required"),
    payload: Yup.number().required("Please supply available payload").moreThan(0, "Flight must support some payload"),
    passengerLimit: passengerLimitShape,
    aircraft: Yup.array().nullable().min(1, "At least one Aircraft required").required("Aircraft is required"),
};

const supplierChangeCharterShape = {
    ...changeCharterShape,
    price: Yup.number()
        .nullable()
        .transform((value, originalValue) => originalValue === '' ? null : value)
        .required("Price is required. You don't want to do this for free, do you?")
        .positive('Price must be greater than zero'),
    routes: Yup.array().of(
        Yup.object().shape(supplierChangeCharterRouteShape)
    ).required("Must have route")
};

const aircraftShape = {
    id: Yup.string(),
    aircraftCategory: Yup.string().transform(value => value === null ? '' : value).trim().required('Category is required'),
    licenseNumber: Yup.string().transform(value => value === null ? '' : value).trim().required('Registration Number is required'),
    type: Yup.string().transform(value => value === null ? '' : value).trim().required('Make and Model is required'),
    passengerLimit: Yup.string(),
    aircraftNotes: Yup.string()

};


let aircraftShapeYup = Yup.object().shape(aircraftShape);

const aircraftImageShape = {
    supplierId: Yup.string().required(),

    category: Yup.string().required(),
    fileName: Yup.string().required(),
    file: Yup.string().required(),
    contentType: Yup.string(),
};
let aircraftImageShapeYup = Yup.object().shape(aircraftImageShape);

const placeBidRoute = {
    viewDepartDate: Yup.date().nullable().transform((value, originalValue) =>
        originalValue == null || (typeof originalValue === 'string' && originalValue.trim() === '') ? null : value
    ).typeError("Invalid Date supplied").required("Depart Date is required"),
    viewDepartTime: Yup.date().nullable().transform((value, originalValue) =>
        originalValue == null || (typeof originalValue === 'string' && originalValue.trim() === '') ? null : value
    ).typeError("Invalid departure time supplied").required("Depart Time is required"),
    flightTime: Yup.string().required("Flight Time is required"),
    payload: Yup.number().required("Please supply available payload").moreThan(0, "Flight must support some payload"),
    passengerLimit: passengerLimitShape,
    aircraft: Yup.array().nullable().min(1, "At least one Aircraft required").required("Aircraft is required"),
};

let createCharterRequestYup = Yup.object().shape(createCharterRequestShape);
let supplierChangeCharterYup = Yup.object().shape(supplierChangeCharterShape);
// Bidding window doesn't apply once a charter is being changed
let clientChangeCharterYup = Yup.object().shape(changeCharterShape);

const placeBidShape = {
    bidderId: Yup.string().required("Supplier is required"),
    inclusions: Yup.string(),
    exclusions: Yup.string(),
    notes: Yup.string(),
    flightTimes: Yup.string(),
    status: Yup.string().required(),
    currency: Yup.string().required("Please supply a currency"),
    price: Yup.number()
        .nullable()
        .transform((value, originalValue) => originalValue === '' ? null : value)
        .required("Price is required. You don't want to do this for free, do you?")
        .positive('Price must be greater than zero'),
    routes: Yup.array().of(
        Yup.object().shape({
            ...placeBidRoute,
            parent: Yup.ref('routes'),
            flightTime: Yup.string().required().test("nonoverlapping", "You have overlapping flight times", (value, context) => {
                const i = parseInt(/(\[)(\d+)(?=\])/.exec(context.path)?.[2] ?? "0");
                const list = ((context as any).from[1].value as { routes: { flightTime: string, viewDepartDate: Date }[] }).routes;
                if (i >= list.length - 1) return true;
                const timeGap = moment.duration(moment(list[i + 1].viewDepartDate).diff(list[i].viewDepartDate));
                const [hours, minutes] = (list[i].flightTime ?? "00:00").split(':');
                const flightDuration = duration(parseInt(hours), "hours").add(minutes, "minutes");
                return timeGap.asMinutes() >= flightDuration.asMinutes();
            }).required("Flight time is a required field")
        })).required("Must have route")
}
let placeBidShapeYup = Yup.object().shape(placeBidShape);


export {
    indicativePriceShapeYup as indicitavePriceShapeYup, emailIndicativeShapeYup as emailIndicitaveShapeYup, manifestShapeYup, resetMyPasswordShapeYup,
    createCharterRequestYup, supplierChangeCharterYup, clientChangeCharterYup, createRouteShapeYup, supplierShapeYup, clientShapeYup, adminShapeYup, charterRequestManifestShapeYup,
    clientDepartmentShapeYup, clientStaffShapeYup, allowedActionsShapeYup, newEmailShapeYup,
    placeBidShapeYup, aircraftShapeYup, resetPasswordShapeYup, aircraftImageShapeYup, inviteCharterAdminShapeYup,
    inviteClientDepartmentShapeYup, inviteClientShapeYup, inviteClientStaffShapeYup, inviteSupplierShapeYup, inviteAdminShapeYup
};